/**
 * MKS-393 — the merch size-chart API client.
 *
 * One endpoint family, two audiences:
 *
 *   - `GET /merch-size-charts` and `GET /merch-size-charts/:item/url` are
 *     `authenticate` only, because every person editing their own sizes on
 *     /profile has to be able to open a chart;
 *   - `POST` and `DELETE /merch-size-charts/:item` are `requireHR`, so only
 *     HR and admin can upload, replace or remove one.
 */
import { apiDelete, apiGet, apiUpload } from './client';
import type { MerchChartItem, MerchChartSummary } from '../../constants/merchSizeCharts';

export type { MerchChartSummary };

export interface MerchChartUrl {
  url: string;
  contentType: string;
  fileName: string;
  expiresInSeconds: number;
}

export function fetchMerchSizeCharts(): Promise<{ data: MerchChartSummary[] }> {
  return apiGet<{ data: MerchChartSummary[] }>('/merch-size-charts');
}

/**
 * Asked for only when somebody actually opens a chart, never on page load:
 * the URL is signed and short-lived, so pre-fetching four of them per
 * /profile visit would mostly hand out URLs that expire unused.
 */
export function fetchMerchSizeChartUrl(item: MerchChartItem): Promise<{ data: MerchChartUrl }> {
  return apiGet<{ data: MerchChartUrl }>(`/merch-size-charts/${item}/url`);
}

export function uploadMerchSizeChart(
  item: MerchChartItem,
  file: File,
): Promise<{ data: MerchChartSummary }> {
  return apiUpload<{ data: MerchChartSummary }>(`/merch-size-charts/${item}`, file);
}

export function deleteMerchSizeChart(
  item: MerchChartItem,
): Promise<{ data: { success: boolean } }> {
  return apiDelete<{ data: { success: boolean } }>(`/merch-size-charts/${item}`);
}
